"use client";

import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  styled,
  Divider,
  Box,
  Chip,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { motion, AnimatePresence } from "framer-motion";
import { Roboto } from "next/font/google";
import "../app/globals.css";
import Link from "next/link";
import { useTranslation } from "react-i18next";

const roboto = Roboto({
  subsets: ["latin"],
  weight: ["400", "500", "700"],
});

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: "none",
  "&:before": { display: "none" },
  "&:not(:last-child)": {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  background: "transparent",
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  "& .MuiAccordionSummary-content": { margin: theme.spacing(0) },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: theme.palette.text.secondary,
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0, 0, 2, 0),
}));

const FrequentlyAskedQuestion = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const faqs = [
    {
      question: t("faq.q1"),
      answer: t("faq.a1"),
    },
    {
      question: t("faq.q2"),
      answer: t("faq.a2"),
    },
    {
      question: t("faq.q3"),
      answer: t("faq.a3"),
    },
    {
      question: t("faq.q4"),
      answer: t("faq.a4"),
    },
    {
      question: t("faq.q5"),
      answer: (
        <>
          <Link
            href="/zayn-ai-recruiter"
            style={{
              color: "#8B5CF6",
              textDecoration: "underline",
              fontWeight: 500,
            }}
          >
            {t("faq.a5Link")}
          </Link>
          {t("faq.a5Rest")}
        </>
      ),
    },
    {
      question: t("faq.q6"),
      answer: t("faq.a6"),
    },
    {
      question: t("faq.q7"),
      answer: t("faq.a7"),
    },
    {
      question: t("faq.q8"),
      answer: (
        <>
          {t("faq.a8Start")}{" "}
          <Link
            href="https://www.candidate-portal.getgeek.ai/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#8B5CF6",
              textDecoration: "underline",
              fontWeight: 500,
            }}
          >
            {t("faq.a8Link")}
          </Link>{" "}
          {t("faq.a8Rest")}
        </>
      ),
    },
    {
      question: t("faq.q9"),
      answer: t("faq.a9"),
    },
    {
      question: t("faq.q10"),
      answer: t("faq.a10"),
    },
    {
      question: t("faq.q11"),
      answer: t("faq.a11"),
    },
    {
      question: t("faq.q12"),
      answer: t("faq.a12"),
    },
    {
      question: t("faq.q13"),
      answer: t("faq.a13"),
    },
    {
      question: t("faq.q14"),
      answer: t("faq.a14"),
    },
    {
      question: t("faq.q15"),
      answer: (
        <>
          {t("faq.a15Start")}{" "}
          <Link
            href="https://getgeek.ai/free-trial-of-ai-recruiting-tool"
            // target="_blank"
            // rel="noopener noreferrer"
            style={{
              color: "#8B5CF6",
              textDecoration: "underline",
              fontWeight: 500,
            }}
          >
            {t("faq.a15Link1")}
          </Link>{" "}
          {t("faq.a15Mid")}{" "}
          <Link
            href="/free-trial-of-ai-recruiting-tool"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#8B5CF6",
              textDecoration: "underline",
              fontWeight: 500,
            }}
          >
            {t("faq.a15Link2")}
          </Link>{" "}
          {t("faq.a15End")}
        </>
      ),
    },
  ];

  const [expanded, setExpanded] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const INITIAL_FAQ_COUNT = 6;

  const visibleFaqs = showAll ? faqs : faqs.slice(0, INITIAL_FAQ_COUNT);

  const handleChange = (index) => (event, isExpanded) => {
    setExpanded(isExpanded ? index : false);
  };

  return (
    <Box sx={{ direction: isArabic ? "rtl" : "ltr" }}>
      <Container
        maxWidth="md"
        component={motion.div}
        initial="hidden"
        sx={{
          py: { xs: 4, sm: 6, md: 8, lg: 5, xl: 8 },
          mt: { xs: "2%", sm: "2%", md: "1%", lg: "-2%", xl: "1%" },
          px: { xs: 2.5, sm: 3 },
        }}
      >
        {/* Chip */}
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", md: "center" },
          }}
        >
          <Chip
            label={t("faq.chip")}
            sx={{
              backgroundColor: "white",
              border: "1px solid #0206174D",
              mb: { xs: 2, sm: 2.5, md: 3 },
              color: "#647482",
              fontSize: { xs: "11px", sm: "13px", md: "16px" },
              fontWeight: "400",
              textTransform: "capitalize",
              px: { xs: 1, sm: 1 },
              py: { xs: 1, sm: 1.5, md: 1 },
              fontFamily: "var(--font-outfit)",
              borderRadius: "40px",
              textAlign: "center",
              height: "auto",
            }}
          />
        </Box>

        {/* Title */}
        <Typography
          component="h2"
          sx={{
            mb: { xs: 3, sm: 4, md: 6 },
            fontSize: {
              xs: "1.4rem",
              sm: "1.75rem",
              md: "35px",
              lg: "35px",
              xl: "40px",
            },
            color: "#0F172A",
            fontFamily: "var(--font-plusjakarta)",
            maxWidth: { xs: "100%", md: 700 },
            textAlign: { xs: isArabic ? "right" : "left", md: "center" },
            mx: { xs: 0, md: "auto" },
            lineHeight: { xs: "1.4", md: "1.3" },
            fontWeight: 600,
          }}
        >
          {/* 
            English: "Everything [You] Need To Know About [AI Recruiting Tool]"
            Arabic:  "كل [ما تحتاج] معرفته عن [أداة التوظيف بالذكاء الاصطناعي]"
            Colors must match exactly — dark / purple / dark / purple
          */}
          {t("faq.title1")}{" "}
          <span style={{ color: "#775CE5" }}>{t("faq.titleHighlight1")}</span>{" "}
          {t("faq.title2")}{" "}
          <span style={{ color: "#775CE5" }}>{t("faq.titleHighlight2")}</span>
        </Typography>

        <Divider sx={{ mb: ".2" }} />

        {/* FAQ List */}
        <AnimatePresence>
          {visibleFaqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <StyledAccordion
                disableGutters
                expanded={expanded === index}
                onChange={handleChange(index)}
              >
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    flexDirection: isArabic ? "row" : "row",
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      marginLeft: isArabic ? "0" : "auto",
                      marginRight: isArabic ? "auto" : "0",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontFamily: "var(--font-outfit)",
                      fontSize: { xs: "13px", sm: "15px", md: "18px" },
                      lineHeight: { xs: "1.5", sm: "1.6" },
                      textAlign: isArabic ? "right" : "left",
                      width: "100%",
                    }}
                  >
                    {faq.question}
                  </Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Typography
                    color="text.secondary"
                    sx={{
                      fontFamily: "var(--font-outfit)",
                      fontSize: { xs: "12px", sm: "14px", md: "18px" },
                      lineHeight: { xs: "1.6", sm: "1.7" },
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </StyledAccordionDetails>
              </StyledAccordion>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* See More / See Less */}
        {faqs.length > INITIAL_FAQ_COUNT && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: { xs: 3, sm: 4, md: 5 },
            }}
          >
            <Button
              component={motion.button}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAll(!showAll)}
              sx={{
                bgcolor: "#FFFFFF",
                color: "#5B6B77",
                textTransform: "none",
                px: { xs: 2.5, sm: 3 },
                py: { xs: 1.2, sm: 1.5 },
                fontFamily: "var(--font-outfit)",
                fontSize: { xs: "0.85rem", sm: "0.9rem", md: "1rem" },
                fontWeight: "500",
                borderRadius: "28px",
                border: "1px solid #E2E8F0",
                boxShadow: "none",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  bgcolor: "#FFFFFF",
                  boxShadow: "none",
                  transform: "scale(1.05)",
                },
              }}
            >
              {showAll ? t("faq.seeLess") : t("faq.seeMore")}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default FrequentlyAskedQuestion;
